/*
 * PageShell — the single responsive frame every primary route inherits.
 *
 * WHY: Historically each page hard-coded its own `<div className="p-4 md:p-8
 * max-w-[1400|1600|1800]px animate-fade-up">` wrapper with no `mx-auto`, so
 * on wide monitors content sat left-weighted with a huge dead gutter on the
 * right, and the max-width drifted from page to page. PageShell fixes both:
 * one padded, centered, animation-preserving frame with a small set of
 * intentional size variants for content-heavy vs. board-heavy screens.
 *
 * Widths track the historic intent so no page suddenly "grows" or "shrinks"
 * more than necessary — they're just now centered inside the canvas the
 * sidebar leaves behind, and use a responsive padding ramp that matches the
 * app's editorial density.
 *
 * Usage:
 *   <PageShell size="default" testid="clients-page">…</PageShell>
 *
 * Sizes:
 *   read      — long-form reading (≤ 896px)
 *   narrow    — settings-like forms (≤ 1024px)
 *   content   — mid-density surfaces (≤ 1152px)
 *   standard  — Advisory Desk, Method, Voice Library (≤ 1400px)
 *   default   — dashboards, lists, workspaces (≤ 1600px)
 *   board     — kanban / pipeline (≤ 1800px)
 *   full      — no cap, fill the canvas
 */
const SIZE_CLASSES = {
  read: "max-w-4xl",
  narrow: "max-w-5xl",
  content: "max-w-6xl",
  standard: "max-w-[1400px]",
  default: "max-w-[1600px]",
  board: "max-w-[1800px]",
  full: "max-w-none",
};

export default function PageShell({
  children,
  size = "default",
  className = "",
  testid,
  as: Tag = "div",
  ...rest
}) {
  const width = SIZE_CLASSES[size] ?? SIZE_CLASSES.default;
  return (
    <Tag
      data-testid={testid}
      className={`w-full mx-auto p-4 md:p-6 lg:p-8 xl:p-10 animate-fade-up ${width} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
